// The widget's display language. The host page sets it; the widget never
// offers its own switch. A module-level value: t() and the formatters read it
// at render time, and render() remounts the widget when the host switches
// (watchLang below), so no label rendered in the old language survives.

export type Lang = 'de' | 'en';

declare global {
  interface Window {
    /** Set by the DZ website: "de" | "en". */
    language?: string;
  }
}

let current: Lang = 'de';

export function setLang(lang: Lang): void {
  current = lang;
}

export function getLang(): Lang {
  return current;
}

/** BCP 47 tag for Intl/toLocaleString. en-GB, not en-US: day-first dates and
    the € conventions of a European readership. */
export function getLocale(): string {
  return current === 'en' ? 'en-GB' : 'de-DE';
}

function normalise(value: string | null | undefined): Lang | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === 'en' || v.startsWith('en-')) return 'en';
  if (v === 'de' || v.startsWith('de-')) return 'de';
  return null;
}

/**
 * The language a container should mount in. Precedence:
 *   1. data-lang on the container (explicit per-embed override),
 *   2. window.language (what the DZ website exposes),
 *   3. the host document's <html lang>,
 *   4. German.
 * Unknown values fall through to the next source rather than to English.
 */
export function resolveLang(container?: Element | null): Lang {
  return (
    normalise(container?.getAttribute('data-lang')) ??
    normalise(typeof window !== 'undefined' ? window.language : null) ??
    normalise(typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null) ??
    'de'
  );
}

// --- Live switching ---------------------------------------------------------
// The DZ website can change its language without reloading the page. Three
// signals cover the ways it may do so: an assignment to window.language (seen
// through an accessor installed on first use), a change of <html lang>, and a
// change of the container's data-lang. Each re-resolves the language; the
// callback runs only when the result differs from what the widget shows.

type Listener = () => void;
const listeners = new Set<Listener>();
let installed = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function notifyAll(): void {
  listeners.forEach(l => l());
}

function installHostWatch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  // window.language: wrap the property in an accessor so a plain assignment
  // notifies us. A host-defined accessor keeps working (its get/set are
  // chained). A non-configurable property (a top-level `var language`) cannot
  // be wrapped; poll it instead, which is a single read per second.
  const desc = Object.getOwnPropertyDescriptor(window, 'language');
  if (!desc || desc.configurable) {
    let value = desc && 'value' in desc ? desc.value : undefined;
    Object.defineProperty(window, 'language', {
      configurable: true,
      enumerable: desc?.enumerable ?? true,
      get: desc?.get ? desc.get.bind(window) : () => value,
      set: (v: string | undefined) => {
        if (desc?.set) desc.set.call(window, v);
        else value = v;
        notifyAll();
      },
    });
  } else {
    let last = window.language;
    pollTimer = setInterval(() => {
      if (window.language !== last) {
        last = window.language;
        notifyAll();
      }
    }, 1000);
  }
  new MutationObserver(notifyAll).observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
}

/**
 * Calls onChange(lang) whenever the host page switches the language this
 * container resolves to (see resolveLang). Returns the unsubscribe function.
 * Not used when the embedder passed an explicit language prop.
 */
export function watchLang(container: Element, onChange: (lang: Lang) => void): () => void {
  installHostWatch();
  let shown = resolveLang(container);
  const check = () => {
    const next = resolveLang(container);
    if (next === shown) return;
    shown = next;
    onChange(next);
  };
  listeners.add(check);
  const own = new MutationObserver(check);
  own.observe(container, { attributes: true, attributeFilter: ['data-lang'] });
  return () => {
    listeners.delete(check);
    own.disconnect();
    if (listeners.size === 0 && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
}
